import { useEffect, useState } from 'react';

import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';
import { useAuthStatus } from '@/contexts/auth';
import { holdFlagOpen } from '@/hooks/heldOpenFlags';
import { useIdentifiedFlagAnswer } from '@/hooks/useIdentifiedFlagAnswer';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

export type FeatureFlagGateState = 'pending' | 'enabled' | 'disabled' | 'unresolved';

/**
 * A blocked or failing flags request never answers, so the wait for PostHog's answer for the
 * signed-in user has to end somewhere. Past it the gate reports `unresolved`, not `disabled`,
 * because nothing has refused the account.
 */
export const FLAG_UNRESOLVED_TIMEOUT_MS = 5000;

/**
 * Whether a flag-guarded page may render yet.
 *
 * `enabled` wins at any moment, including after `disabled`, because the PostHog hook stays
 * subscribed and a late answer must still open the page. A false or missing flag counts as
 * `disabled` only once PostHog has answered for the identified user, because the anonymous answer
 * that comes first can differ; a late identified answer also replaces `unresolved`. Neither state
 * falls back to `pending`, so the message never flickers into a loader.
 *
 * Once the page has opened it stays open for this mount until this tab signs out. PostHog keeps
 * its flags in storage that every tab shares, so signing out in another tab can turn the flag off
 * here and would unmount a page holding unsaved edits. The cost: a flag turned off in PostHog
 * closes only pages opened after that.
 */
export function useFeatureFlagGate(flag: PostHogFeatureFlag): FeatureFlagGateState {
    const { enabled } = usePostHogFeatureFlag(flag);
    const { isAuthenticated, isAuthenticating } = useAuthStatus();
    const signedOut = !isAuthenticated && !isAuthenticating;
    // Keyed by flag, like the timeout below, so another flag never opens on this one's answer.
    // Set while rendering so the render in which the flag turns off already sees it.
    const [openedFlag, setOpenedFlag] = useState<PostHogFeatureFlag | null>(null);
    if (enabled && !signedOut && openedFlag !== flag) setOpenedFlag(flag);
    if (signedOut && openedFlag !== null) setOpenedFlag(null);
    const opened = openedFlag === flag && !signedOut;
    const waiting = !isAuthenticating && !enabled && !opened;
    const answered = useIdentifiedFlagAnswer(waiting);
    // Keyed by flag so a different flag starts its own wait instead of inheriting this one.
    const [timedOutFlag, setTimedOutFlag] = useState<PostHogFeatureFlag | null>(null);
    const timedOut = timedOutFlag === flag;

    useEffect(() => {
        if (!waiting || answered || timedOut) return undefined;
        const timer = setTimeout(() => setTimedOutFlag(flag), FLAG_UNRESOLVED_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [flag, waiting, answered, timedOut]);

    // Links to the open page read the same flag; holding it here keeps them in step with the page.
    useEffect(() => (opened ? holdFlagOpen(flag) : undefined), [opened, flag]);

    useEffect(() => {
        // A flag that turns off later in the session gets its own wait, not one that expired earlier.
        if (enabled) setTimedOutFlag(null);
    }, [enabled]);

    if (enabled || opened) return 'enabled';
    if (isAuthenticating) return 'pending';
    if (answered) return 'disabled';
    if (timedOut) return 'unresolved';
    return 'pending';
}
